/**
 * Files, as their own thing.
 *
 * This started attached to the import tab, on the reasoning that a viewer
 * should fit the existing flow rather than become a feature. That was too
 * tight: it meant a file could only be looked at if you were willing to import
 * it, and importing writes millions of rows into ClickHouse. Opening a file to
 * see what is in it is a different job from feeding it to the lead pipeline,
 * and the two should not be the same button.
 *
 * So a file gets here two ways: uploaded straight here to look at, or promoted
 * from an import that already happened. Both end up as the same thing.
 */
import { Fragment, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActionIcon, Alert, Badge, Button, Card, Collapse, Group, Loader, Modal, Pagination, Progress,
  SegmentedControl, Select, Stack, Table, Text, TextInput, Title, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconChevronRight, IconCornerDownRight, IconFileUpload, IconSearch, IconTable, IconTrash,
} from '@tabler/icons-react';
import { useCampaigns, useCreateSheet, useDeleteSheet, useEntities, useImports, useSheets } from '../api/queries';
import { uploadSheetCsv } from '../api/upload';
import { formatDateTime, formatNumber } from '../theme';
import { ImportJobCard } from './ImportJobCard';
import { SheetHistoryPanel } from './SheetHistoryPanel';
import { useAuth } from '../auth/AuthProvider';
import { useVocab } from '../vocab';

type SheetRow = NonNullable<ReturnType<typeof useSheets>['data']>['sheets'][number];
const PAGE_SIZE = 25;
/** The filter value for "no supplier" and "not imported", which no id can be. */
const NONE = '__none__';

/**
 * The list in the order it is shown: each file followed by the files made
 * from it, indented, so a big file and everything cut from it read as one
 * family instead of scattering through the list by date. A file whose parent
 * is not in view (filtered out, or removed) stands at the top level.
 */
function family(rows: SheetRow[]): { sheet: SheetRow; depth: number }[] {
  const ids = new Set(rows.map((r) => r.id));
  const children = new Map<string, SheetRow[]>();
  for (const r of rows) {
    if (r.parent_sheet_id && ids.has(r.parent_sheet_id)) {
      const list = children.get(r.parent_sheet_id) ?? [];
      list.push(r);
      children.set(r.parent_sheet_id, list);
    }
  }
  const out: { sheet: SheetRow; depth: number }[] = [];
  const walk = (r: SheetRow, depth: number) => {
    out.push({ sheet: r, depth });
    for (const c of children.get(r.id) ?? []) walk(c, depth + 1);
  };
  for (const r of rows) {
    if (!(r.parent_sheet_id && ids.has(r.parent_sheet_id))) walk(r, 0);
  }
  return out;
}

function StatusBadge({ status }: { status: string }) {
  const colour =
    status === 'ready' ? 'chartGreen' : status === 'failed' ? 'chartRed' : 'chartAmber';
  const label = status === 'preparing' ? 'preparing' : status;
  return <Badge size="sm" variant="light" color={colour}>{label}</Badge>;
}

/** Imports still running, or stopped and waiting on someone, whichever file they came from. */
function RunningImports() {
  const imports = useImports(25, { watch: true });
  const running = (imports.data?.imports ?? []).filter((j) =>
    ['pending', 'processing', 'paused'].includes(j.status),
  );
  if (running.length === 0) return null;
  return (
    <Stack gap="xs">
      {running.map((j) => <ImportJobCard key={j.job_id} jobId={j.job_id} />)}
    </Stack>
  );
}

export function SheetsPage() {
  const v = useVocab();
  const sheets = useSheets();
  const create = useCreateSheet();
  const remove = useDeleteSheet();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [pct, setPct] = useState<number | null>(null);
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);
  /* Which file is showing its history. One at a time — a page of expanded
     rows is a page nobody can scan. */
  const [openId, setOpenId] = useState<string | null>(null);
  const { user } = useAuth();
  const me = user?.email ?? '';
  const [search, setSearch] = useState('');
  // Your own files first: a shared list of everything anyone ever uploaded
  // is one nobody can find anything in.
  const [whoseChoice, setWhose] = useState<'mine' | 'everyone' | null>(null);
  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  const [campaignFilter, setCampaignFilter] = useState<string>('all');
  const suppliers = useEntities('suppliers');
  const campaigns = useCampaigns();
  // A chosen file waits here while its supplier is asked for.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingSupplier, setPendingSupplier] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  async function choose(file: File, supplierId: string | null) {
    setPct(0);
    try {
      const { fileKey } = await uploadSheetCsv(file, setPct);
      const sheet = await create.mutateAsync({
        file_key: fileKey,
        file_name: file.name,
        supplier_id: supplierId,
      });
      setPct(null);
      // Straight into it: the page polls while it converts, so there is
      // nothing useful to do back here in the meantime.
      navigate(`/sheets/${sheet.id}`);
    } catch (e) {
      setPct(null);
      notifications.show({
        color: 'red',
        title: 'Could not open that file',
        message: (e as Error).message,
      });
    }
  }

  const all = useMemo(() => sheets.data?.sheets ?? [], [sheets.data]);
  const mineCount = all.filter((s) => s.created_by === me).length;
  // Until someone chooses, start on their own files if they have any, and on
  // everyone's if they do not, rather than opening on an empty list.
  const whose = whoseChoice ?? (mineCount > 0 ? 'mine' : 'everyone');
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matching = all.filter(
      (s) =>
        (whose === 'everyone' || s.created_by === me) &&
        (supplierFilter === 'all' ||
          (supplierFilter === NONE ? !s.supplier_id : s.supplier_id === supplierFilter)) &&
        (campaignFilter === 'all' ||
          (s.imported_into ?? []).some((c) => c.campaign_id === campaignFilter)) &&
        (!q || s.file_name.toLowerCase().includes(q) || (s.description ?? '').toLowerCase().includes(q)),
    );
    return family(matching);
  }, [all, whose, supplierFilter, campaignFilter, search, me]);
  const pages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const visible = shown.slice((Math.min(page, pages) - 1) * PAGE_SIZE, Math.min(page, pages) * PAGE_SIZE);
  const rows = visible;

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Title order={2}>Imports</Title>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.currentTarget.files?.[0];
            e.currentTarget.value = '';
            if (f) {
              // Default to the supplier being looked at, if one is filtered on.
              setPendingSupplier(supplierFilter !== 'all' && supplierFilter !== NONE ? supplierFilter : null);
              setPendingFile(f);
            }
          }}
        />
        <Button
          leftSection={<IconFileUpload size={16} />}
          onClick={() => inputRef.current?.click()}
          loading={pct !== null || create.isPending}
        >
          Open a CSV
        </Button>
      </Group>

      <Text size="sm" c="dimmed">
        Open a CSV of any size to look through it. The file is never sent to your browser,
        so a ten million row file opens as quickly as a small one. Opening a file here does
        not import anything.
      </Text>

      <RunningImports />

      {pct !== null && (
        <Card padding="sm">
          <Stack gap={6}>
            <Text size="sm">Uploading</Text>
            <Progress value={pct} striped animated />
          </Stack>
        </Card>
      )}

      {sheets.error && (
        <Alert color="red" variant="light">{sheets.error.message}</Alert>
      )}

      <Group gap="sm" wrap="wrap">
        <TextInput
          placeholder="Search names and descriptions"
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => { setSearch(e.currentTarget.value); setPage(1); }}
          w={280}
        />
        <SegmentedControl
          size="xs"
          value={whose}
          onChange={(v) => { setWhose(v as 'mine' | 'everyone'); setPage(1); }}
          data={[
            { value: 'mine', label: `Mine (${mineCount})` },
            { value: 'everyone', label: `Everyone (${all.length})` },
          ]}
        />
        <Select
          size="xs"
          w={200}
          value={supplierFilter}
          onChange={(value) => { setSupplierFilter(value ?? 'all'); setPage(1); }}
          allowDeselect={false}
          data={[
            { value: 'all', label: `Any ${v.one('supplier')}` },
            { value: NONE, label: `No ${v.one('supplier')}` },
            ...(suppliers.data?.entities ?? []).map((sp) => ({ value: sp.id, label: sp.name })),
          ]}
        />
        <Select
          size="xs"
          w={220}
          value={campaignFilter}
          onChange={(value) => { setCampaignFilter(value ?? 'all'); setPage(1); }}
          allowDeselect={false}
          data={[
            { value: 'all', label: `Any ${v.one('campaign')}` },
            ...(campaigns.data?.campaigns ?? []).map((c) => ({ value: c.id, label: c.name })),
          ]}
        />
      </Group>

      <Card p={0}>
        <Table.ScrollContainer minWidth={720}>
          <Table striped highlightOnHover horizontalSpacing={10} verticalSpacing={8}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>File</Table.Th>
                <Table.Th w={160}>{v.One('supplier')}</Table.Th>
                <Table.Th w={100} ta="right">Rows</Table.Th>
                <Table.Th w={170}>By</Table.Th>
                <Table.Th w={140}>Added</Table.Th>
                <Table.Th w={40} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sheets.isPending && (
                <Table.Tr><Table.Td colSpan={6}>
                  <Group justify="center" py="lg"><Loader size="sm" /></Group>
                </Table.Td></Table.Tr>
              )}
              {!sheets.isPending && rows.length === 0 && (
                <Table.Tr><Table.Td colSpan={6}>
                  <Stack align="center" gap={6} py="xl">
                    <Text c="dimmed" size="sm">
                      {all.length === 0
                        ? 'No files yet. Open a CSV to look through it.'
                        : whose === 'mine' && mineCount === 0
                          ? 'You have not uploaded or made any files yet.'
                          : 'No files match.'}
                    </Text>
                    {whose === 'mine' && all.length > 0 && (
                      <Button size="compact-sm" variant="subtle" onClick={() => setWhose('everyone')}>
                        Show everyone&apos;s files
                      </Button>
                    )}
                  </Stack>
                </Table.Td></Table.Tr>
              )}
              {rows.map(({ sheet: s, depth }) => (
                <Fragment key={s.id}>
                <Table.Tr
                  style={{ cursor: 'pointer' }}
                  onClick={() => setOpenId((o) => (o === s.id ? null : s.id))}
                >
                  <Table.Td style={{ paddingLeft: 10 + depth * 22 }}>
                    <Group gap={8} wrap="nowrap" align="flex-start">
                      {depth > 0 && (
                        <IconCornerDownRight size={14} style={{ opacity: 0.4, flexShrink: 0, marginTop: 3 }} />
                      )}
                      <IconChevronRight
                        size={14}
                        style={{
                          opacity: 0.5,
                          flexShrink: 0,
                          transform: openId === s.id ? 'rotate(90deg)' : undefined,
                          transition: 'transform 120ms',
                        }}
                      />
                      <IconTable size={15} style={{ opacity: 0.55, flexShrink: 0 }} />
                      <div style={{ minWidth: 0 }}>
                        <Group gap={6} wrap="nowrap">
                          <Text
                            size="sm"
                            fw={500}
                            truncate
                            style={{ textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                            onClick={(e) => { e.stopPropagation(); navigate(`/sheets/${s.id}`); }}
                          >
                            {s.file_name}
                          </Text>
                          {s.status !== 'ready' && <StatusBadge status={s.status} />}
                        </Group>
                        {s.description && (
                          <Text size="xs" c="dimmed" lineClamp={1}>{s.description}</Text>
                        )}
                        {s.parent_sheet_id && (
                          <Text size="xs" c="dimmed" lineClamp={1}>
                            from {s.parent_name ?? 'a removed file'}
                          </Text>
                        )}
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" truncate c={s.supplier_name ? undefined : 'dimmed'}>
                      {s.supplier_name ?? '-'}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm">
                      {s.status === 'ready' ? formatNumber(s.row_count) : '-'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed" truncate>
                      {s.created_by ? (s.created_by === me ? 'You' : s.created_by) : 'Not recorded'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{formatDateTime(s.created_at)}</Text>
                  </Table.Td>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Tooltip label="Remove from this list">
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        aria-label={`Remove ${s.file_name}`}
                        onClick={() => setRemoving({ id: s.id, name: s.file_name })}
                      >
                        <IconTrash size={15} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td colSpan={6} p={0} style={{ borderTop: 'none' }}>
                    <Collapse in={openId === s.id}>
                      {/* Mounted only while open, so a list of files does not
                          fetch a history nobody asked to see. */}
                      {openId === s.id && <SheetHistoryPanel sheetId={s.id} fileName={s.file_name} />}
                    </Collapse>
                  </Table.Td>
                </Table.Tr>
                </Fragment>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      {pages > 1 && (
        <Group justify="flex-end">
          <Pagination size="sm" total={pages} value={Math.min(page, pages)} onChange={setPage} />
        </Group>
      )}

      <Modal
        opened={pendingFile !== null}
        onClose={() => setPendingFile(null)}
        title="Who sent this file?"
        size="sm"
      >
        <Stack>
          <Text size="sm">
            <b>{pendingFile?.name}</b>
            {`. Files are sorted by ${v.one('supplier')}, and anything made from this file or imported from it carries the same one.`}
          </Text>
          <Select
            label={v.One('supplier')}
            placeholder={`No ${v.one('supplier')}`}
            searchable
            clearable
            data={(suppliers.data?.entities ?? []).filter((sp) => sp.active).map((sp) => ({ value: sp.id, label: sp.name }))}
            value={pendingSupplier}
            onChange={setPendingSupplier}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setPendingFile(null)}>Cancel</Button>
            <Button
              onClick={() => {
                const f = pendingFile;
                setPendingFile(null);
                if (f) void choose(f, pendingSupplier);
              }}
            >
              Open file
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={removing !== null}
        onClose={() => setRemoving(null)}
        title="Remove this file?"
        size="sm"
      >
        <Stack>
          <Text size="sm">
            {`${removing?.name ?? ''} will no longer be listed here. This does not touch any ${v.many('lead')} that were imported from it.`}
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setRemoving(null)}>Keep</Button>
            <Button
              color="red"
              loading={remove.isPending}
              onClick={() => {
                if (removing) remove.mutate(removing.id);
                setRemoving(null);
              }}
            >
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
